/**
 * Session Storage Utility
 * Desktop keeps its sealed session in the main process, encrypted with the OS
 * keychain (Electron safeStorage), and reads it synchronously for cross-origin
 * API headers. Desktop builds without that bridge fall back to renderer
 * localStorage. Web uses an HttpOnly cookie and stores only a non-secret UI hint.
 */

import { markSignedInOnThisDevice } from './returningDevice';
import { clearAccessTokenExpiry } from '@/src/features/auth/accessTokenLifetime';

const SESSION_STORAGE_KEY = 'workos_session_id';
const WEB_SESSION_HINT_KEY = 'smartmanager_session_present';

type SessionStorageReader = Pick<Storage, 'getItem'>;
type SessionStorageWriter = Pick<Storage, 'setItem'>;
type SessionStorageRemover = Pick<Storage, 'removeItem'>;

function isElectronRuntime() {
  return typeof window !== 'undefined' && !!window.electronAPI?.isElectron;
}

type DesktopSessionCredentialStore = NonNullable<NonNullable<Window['electronAPI']>['sessionCredential']>;

/** The main-process encrypted store; absent in desktop builds that predate it. */
function desktopCredentialStore(): DesktopSessionCredentialStore | null {
  const store = window.electronAPI?.sessionCredential;
  return store && typeof store.get === 'function' && typeof store.set === 'function' ? store : null;
}

function removeLegacyDesktopSession(): void {
  try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* storage unavailable */ }
}

/**
 * Save the desktop session (encrypted store when available). `signIn` marks a
 * new sign-in, which may be another account, rather than a rotation; the
 * shell reloads its Connections window for one.
 */
export function setSession(sessionId: string, { signIn = false }: { signIn?: boolean } = {}): boolean {
  if (typeof window === 'undefined' || !sessionId.trim()) return false;
  if (!isElectronRuntime()) {
    console.warn('[SessionStorage] Refusing to persist a web authentication credential');
    return false;
  }
  const store = desktopCredentialStore();
  if (store) {
    try {
      if (!store.set(sessionId, { signIn }) || store.get() !== sessionId) return false;
      removeLegacyDesktopSession();
      return true;
    } catch (e) {
      console.error('[SessionStorage] Error saving to the desktop credential store:', e);
      return false;
    }
  }
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    return localStorage.getItem(SESSION_STORAGE_KEY) === sessionId;
  } catch (e) {
    console.error('[SessionStorage] Error saving to localStorage:', e);
    return false;
  }
}

/**
 * Commit a desktop login only when both the sealed session and the removal of
 * stale persisted app state can be verified. Callers must not broadcast login
 * or navigate to the dashboard when this returns false.
 */
export function commitElectronLoginSession(sessionId: string): boolean {
  if (!setSession(sessionId, { signIn: true })) return false;

  try {
    localStorage.removeItem('nv-storage');
    if (localStorage.getItem('nv-storage') !== null || getSession() !== sessionId) {
      throw new Error('Desktop login persistence verification failed');
    }
    markSignedInOnThisDevice();
    return true;
  } catch (error) {
    console.error('[SessionStorage] Error finalizing desktop login:', error);
    clearSession();
    return false;
  }
}

/**
 * Get the desktop session. A session left in localStorage by an older desktop
 * build is moved into the encrypted store once, then removed.
 */
export function getSession(): string | null {
  if (typeof window === 'undefined') return null;
  if (!isElectronRuntime()) return null;
  const store = desktopCredentialStore();
  if (store) {
    try {
      const held = store.get();
      if (held) return held;
      const legacy = localStorage.getItem(SESSION_STORAGE_KEY);
      if (legacy && store.set(legacy)) removeLegacyDesktopSession();
      return legacy;
    } catch (e) {
      console.error('[SessionStorage] Error reading the desktop credential store:', e);
      return null;
    }
  }
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch (e) {
    console.error('[SessionStorage] Error reading from localStorage:', e);
    return null;
  }
}

/**
 * Clear the session from every place it may be held
 */
export function clearSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.electronAPI?.sessionCredential?.clear();
  } catch (e) {
    console.error('[SessionStorage] Error clearing the desktop credential store:', e);
  }
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(WEB_SESSION_HINT_KEY);
  } catch (e) {
    console.error('[SessionStorage] Error clearing localStorage:', e);
  }
  clearAccessTokenExpiry();
  // Sign-out also stops the desktop device-files runtime from serving files.
  try {
    void window.electronAPI?.deviceFiles?.disconnect().catch(() => {});
  } catch {
    // The desktop bridge is optional; web has none.
  }
}

export function markWebSessionPresent(): void {
  if (typeof window === 'undefined' || isElectronRuntime()) return;
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.setItem(WEB_SESSION_HINT_KEY, '1');
  } catch (e) {
    console.warn('[SessionStorage] Browser storage is unavailable:', e);
  }
}

/**
 * Whether this browser holds a signed-in session. `"unknown"` means storage
 * could not be read (blocked or private mode), so callers that must fail
 * closed for signed-in people can treat it as possibly signed in.
 */
export type BrowserSessionPresence = 'present' | 'absent' | 'unknown';

export function readBrowserSessionPresence(): BrowserSessionPresence {
  if (typeof window === 'undefined') return 'absent';
  try {
    const present = isElectronRuntime()
      ? !!getSession()
      : localStorage.getItem(WEB_SESSION_HINT_KEY) === '1';
    return present ? 'present' : 'absent';
  } catch {
    return 'unknown';
  }
}

export function hasBrowserSession(): boolean {
  return readBrowserSessionPresence() === 'present';
}

/** Browser session storage is optional in privacy modes and embedded contexts. */
export function getSessionStorageItem(
  key: string,
  getStorage: () => SessionStorageReader = () => window.sessionStorage,
): string | null {
  try { return getStorage().getItem(key); } catch { return null; }
}

export function setSessionStorageItem(
  key: string,
  value: string,
  getStorage: () => SessionStorageWriter = () => window.sessionStorage,
): boolean {
  try { getStorage().setItem(key, value); return true; } catch { return false; }
}

export function removeSessionStorageItem(
  key: string,
  getStorage: () => SessionStorageRemover = () => window.sessionStorage,
): boolean {
  try { getStorage().removeItem(key); return true; } catch { return false; }
}

export function getInviteTokenHint(): string | null {
  return getSessionStorageItem('invite_token');
}

export function setInviteTokenHint(token: string): boolean {
  return setSessionStorageItem('invite_token', token);
}

export function clearInviteTokenHint(): void {
  removeSessionStorageItem('invite_token');
}
